import { FormattedMessage, useIntl } from "react-intl";
import { type ReactNode, useState } from "react";
import { parsePhoneNumberWithError as parsePhoneNumber } from "libphonenumber-js";
import classnames from "classnames";

import Icon from "common/core/icon";
import { format } from "common/core/format/date";
import { Column, Container, Row } from "common/core/responsive";
import FormattedAddress from "common/core/format/address";
import { Heading } from "common/core/typography";
// eslint-disable-next-line import/no-restricted-paths
import { IdentityAttributeLabels } from "business_portal/trusted_referee/meeting/content/identity_attributes";
import {
  IdentityAttributeName,
  PhotoIdVerificationStatus,
  PhotoIdVerificationVerificationType,
} from "graphql_globals";
import { FormattedValidationSource } from "common/core/format/formatted_validation_source";
import { FormattedVerificationMethod } from "common/core/format/formatted_verification_method";
import { usePermissions } from "common/core/current_user_role";

import type {
  IdentityDetails_documentBundle_DocumentBundle_organizationTransaction_customerSigners_signerIdentities_identityAttributes as IdentityAttribute,
  IdentityDetails_documentBundle_DocumentBundle_organizationTransaction_customerSigners_signerIdentities as TransactionSignerIdentity,
} from "./index.query.graphql";
import Styles from "./index.module.scss";

type BadgeProps = {
  status: PhotoIdVerificationStatus;
  failedVerificationTypes: PhotoIdVerificationVerificationType[];
};

function identityAttributesSorter(a: IdentityAttribute, b: IdentityAttribute) {
  const orderedAttributesByName = [
    IdentityAttributeName.FULL_NAME,
    IdentityAttributeName.HOME_ADDRESS,
    IdentityAttributeName.EMAIL,
    IdentityAttributeName.PHONE,
    IdentityAttributeName.DATE_OF_BIRTH,
  ];

  return orderedAttributesByName.indexOf(a.name) - orderedAttributesByName.indexOf(b.name);
}

function getAttributeValue(attribute: IdentityAttribute) {
  switch (attribute.__typename) {
    case "IdentityAttributeDob":
      return format({ value: attribute.dateOfBirthValue, formatStyle: "MM/dd/yyyy" })!;
    case "IdentityAttributeFullName":
      return attribute.fullNameValue;
    case "IdentityAttributePhoneNumber":
      return parsePhoneNumber(attribute.phoneValue).formatInternational();
    case "IdentityAttributeEmail":
      return attribute.emailValue;
    case "IdentityAttributeHomeAddress":
      return <FormattedAddress address={attribute.homeAddressValue} />;
  }
}

function getAttributeObfuscatedValue(attribute: IdentityAttribute) {
  switch (attribute.__typename) {
    case "IdentityAttributePhoneNumber":
      return `*******${attribute.phoneValue.toString().slice(-4)}`;
    case "IdentityAttributeDob":
      return "**/**/****";
    default:
      return getAttributeValue(attribute);
  }
}

function AttributeItem({ attribute }: { attribute: IdentityAttribute }) {
  const containsSensitiveData = ["IdentityAttributePhoneNumber", "IdentityAttributeDob"].includes(
    attribute.__typename,
  );
  const [reveal, setReveal] = useState(!containsSensitiveData);

  const attributeValue = reveal
    ? getAttributeValue(attribute)
    : getAttributeObfuscatedValue(attribute);

  return (
    <>
      {attributeValue}
      {containsSensitiveData && (
        <Icon
          className={Styles.showHideToggle}
          name={reveal ? "visibility-off" : "witness-filled"}
          onClick={() => setReveal(!reveal)}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: PhotoIdVerificationStatus }) {
  return (
    <div
      className={classnames(Styles.badge, {
        [Styles.badgePassed]: status === PhotoIdVerificationStatus.SUCCESS,
        [Styles.badgeFailed]:
          status === PhotoIdVerificationStatus.FAILURE ||
          status === PhotoIdVerificationStatus.ACTION_REQUIRED,
        [Styles.badgeInfo]:
          status === PhotoIdVerificationStatus.PENDING ||
          status === PhotoIdVerificationStatus.UNKNOWN,
      })}
    >
      {(() => {
        switch (status) {
          case PhotoIdVerificationStatus.SUCCESS:
            return (
              <FormattedMessage id="e389f875-d89a-4f8b-b7c0-bca7ccf02c19" defaultMessage="Passed" />
            );
          case PhotoIdVerificationStatus.FAILURE:
            return (
              <FormattedMessage id="f7a8ae7c-d6fc-4eb8-b044-5507953d2d69" defaultMessage="Failed" />
            );
          case PhotoIdVerificationStatus.PENDING:
            return (
              <FormattedMessage
                id="12353853-d673-4df1-bd47-2f49e0e78f81"
                defaultMessage="Pending"
              />
            );
          case PhotoIdVerificationStatus.UNKNOWN:
            return (
              <FormattedMessage id="1d523778-6a09-4053-b122-d6c48e6f5e17" defaultMessage="N/A" />
            );
          case PhotoIdVerificationStatus.ACTION_REQUIRED:
            return (
              <FormattedMessage
                id="b471edac-2434-4ef2-8d04-4dd60e47130d"
                defaultMessage="Action Required"
              />
            );
        }
      })()}
    </div>
  );
}

function CredentialAnalysisStatusBadge({ status, failedVerificationTypes }: BadgeProps) {
  if (status === PhotoIdVerificationStatus.FAILURE) {
    return (
      <StatusBadge
        status={
          failedVerificationTypes.includes(PhotoIdVerificationVerificationType.ID)
            ? PhotoIdVerificationStatus.FAILURE
            : PhotoIdVerificationStatus.SUCCESS
        }
      />
    );
  }
  return <StatusBadge status={status} />;
}

function BiometricStatusBadge({ status, failedVerificationTypes }: BadgeProps) {
  if (status === PhotoIdVerificationStatus.FAILURE) {
    return (
      <StatusBadge
        status={
          failedVerificationTypes.includes(PhotoIdVerificationVerificationType.SELFIE)
            ? PhotoIdVerificationStatus.FAILURE
            : PhotoIdVerificationStatus.SUCCESS
        }
      />
    );
  }
  if (status === PhotoIdVerificationStatus.SUCCESS) {
    return <StatusBadge status={PhotoIdVerificationStatus.SUCCESS} />;
  }
  return <StatusBadge status={PhotoIdVerificationStatus.UNKNOWN} />;
}

function Col({ children }: { children: ReactNode }) {
  return (
    <Column className={Styles.identityAttributeItem} xs={6} sm={4} lg={3}>
      {children}
    </Column>
  );
}

export default function IdentityProfile({
  signerIdentity,
}: {
  signerIdentity: TransactionSignerIdentity;
}) {
  const intl = useIntl();
  const { hasPermissionFor } = usePermissions();
  const status = signerIdentity.photoId?.statusV2 || PhotoIdVerificationStatus.UNKNOWN;
  const failedVerificationTypes = signerIdentity.photoId?.failedVerificationTypes ?? [];
  return (
    <Container fluid noSpacing>
      <Row>
        {[...(signerIdentity.identityAttributes || [])]
          .sort(identityAttributesSorter)
          .map((attribute) => (
            <Col key={attribute.id}>
              <Heading textStyle="headingFive" level="h4">
                {intl.formatMessage(IdentityAttributeLabels[attribute.name])}
              </Heading>
              <AttributeItem attribute={attribute} />
            </Col>
          ))}
        <Col>
          <Heading textStyle="headingFive" level="h4">
            <FormattedMessage id="77211f4f-605b-40d5-a808-37549871df3d" defaultMessage="User ID" />
          </Heading>
          {signerIdentity.user!.id}
        </Col>
        <Col>
          <Heading textStyle="headingFive" level="h4">
            <FormattedMessage
              id="6d5da7fd-4494-45cb-bf98-dd16335d70d3"
              defaultMessage="Credential analysis"
            />
          </Heading>
          <CredentialAnalysisStatusBadge
            status={status}
            failedVerificationTypes={failedVerificationTypes}
          />
        </Col>
        <Col>
          <Heading textStyle="headingFive" level="h4">
            <FormattedMessage
              id="112e2f9c-8081-4cc7-b7c6-69c1d36e2202"
              defaultMessage="Selfie comparison"
            />
          </Heading>
          <BiometricStatusBadge status={status} failedVerificationTypes={failedVerificationTypes} />
        </Col>
        {signerIdentity.photoId && (
          <>
            <Col>
              <Heading textStyle="headingFive" level="h4">
                <FormattedMessage
                  id="4e8515d6-b4b8-450b-8844-5396a69bff22"
                  defaultMessage="Verification method"
                />
              </Heading>
              <FormattedVerificationMethod
                verificationMethod={signerIdentity.photoId.verificationMethod}
              />
            </Col>
            {hasPermissionFor("viewIdentityDetails") && (
              <Col>
                <Heading textStyle="headingFive" level="h4">
                  <FormattedMessage
                    id="ad4da5d3-858a-46fe-9f7c-8f9bb6ce76cd"
                    defaultMessage="Validation source"
                  />
                </Heading>
                <FormattedValidationSource validationSource={signerIdentity.photoId.provider} />
              </Col>
            )}
          </>
        )}
      </Row>
    </Container>
  );
}
